import { useEffect, useRef, useState } from 'react';
import { Ditado } from '../components/Ditado';
import { Alert, Pressable, ScrollView, Text, TextInput, View } from 'react-native';
import Svg, { Rect, Line, Circle } from 'react-native-svg';
import { useDoc, setDoc } from '../store/store';
import type { Block, Item, Room, TarefaCasa } from '../store/types';
import { ReceivedBoxes } from '../components/BoxCard';
import { FrequenciaEditor, useTaskSheet } from '../components/TaskSheet';
import { ActionSheet, Btn, Chip, Field, Row, Screen, Sheet, type Action } from '../components/ui';
import { Recolhivel } from '../components/Recolhivel';
import { C, alpha, useCores } from '../components/theme';
import { brYmd, norm, today, uid } from '../lib/date';
import { recurLabel } from '../lib/recur';
import { N_, t } from '../lib/i18n';
import { novoBloco } from '../store/anotacoes';
import {
  doComodo, excluirTarefaCasa, gruposCasa, ligarPlanner, marcarFeita, marcarSub, migrarTarefasDoPlanner, moverParaComodo, novaTarefaCasa,
  ordenarComodos, ordenarGrupo, ordenarNoComodo, salvarTarefaCasa, situacaoCasa, tarefasCasa, type Grupo,
} from '../store/casa';
import { Ordenavel, ScrollArrastavel, ZonaSoltar, semArrastar } from '../components/Arrastar';
import { ItemDeslizar, SemDeslizar, excluirPequeno, inserirEm } from '../components/ItemDeslizar';
import { EmojiPicker } from '../components/EmojiPicker';
import { AcoesItem, acoesDoMenu, useAcoes } from '../components/Acoes';
import { Arquivados, useSelecao } from '../components/Selecao';
import { useOrganizar } from '../lib/organizar';
import { paraLixeira } from '../lib/lixeira';
import { useBotaoNovo } from '../lib/botaoNovo';
import { useNovoComNota } from '../components/NotaRapida';
import { temAviso, useMeAvise } from '../lib/meAvise';
import { useCaixas } from '../lib/caixas';
import { ROLAGEM_TECLADO } from '../components/teclado';

export const PRESETS: [string, string][] = [
  [N_('Escritório'), '💻'], [N_('Lavanderia'), '🧺'], [N_('Cozinha'), 'geladeira'], [N_('Sala de estar'), '🛋️'], [N_('Sacada'), '🪴'], [N_('Quintal'), '🌳'],
  [N_('Quarto de casal'), '🛏️'], [N_('Quarto infantil'), '🧸'], [N_('Quarto de hóspedes'), '🛌'], [N_('Hall de entrada'), '🚪'], [N_('Sala de jantar'), '🍽️'],
  [N_('Porão'), '📦'], [N_('Sótão'), '🪜'], [N_('Despensa'), '🥫'], [N_('Corredor'), '🚶'], [N_('Banheiro'), '🚿'], [N_('Suíte'), '🛁'],
];
const ICONES = ['geladeira', '🛋️', '🛏️', '🧸', '🛌', '🛁', '🚿', '🧺', '🍽️', '🍳', '💻', '🪴', '🌳', '🚪', '📦', '🪜', '🥫', '🚶', '🚗', '🐕', '🏊', '🎮', '📚', '🧹'];

export function iconeDoNome(nome: string) {
  const n = norm(nome);
  const regras: [string[], string][] = [
    [['cozinha'], 'geladeira'], [['suite'], '🛁'], [['banheiro', 'lavabo'], '🚿'], [['infantil', 'crianca', 'bebe'], '🧸'],
    [['hospede', 'visita'], '🛌'], [['quarto'], '🛏️'], [['sala de jantar', 'jantar'], '🍽️'], [['sala', 'estar', 'tv'], '🛋️'],
    [['lavanderia', 'area de servico'], '🧺'], [['escritorio', 'home office'], '💻'], [['sacada', 'varanda', 'jardim'], '🪴'],
    [['quintal'], '🌳'], [['hall', 'entrada'], '🚪'], [['porao', 'deposito'], '📦'], [['sotao'], '🪜'], [['despensa'], '🥫'],
    [['corredor'], '🚶'], [['garagem'], '🚗'], [['piscina'], '🏊'],
  ];
  for (const [ws, ic] of regras) if (ws.some(w => n.includes(w))) return ic;
  return '🏠';
}

/** Ícone do cômodo; "geladeira" é desenhado (não existe emoji de geladeira). */
export function RoomIcon({ icon, size = 36, cor = '#10B981' }: { icon: string; size?: number; cor?: string }) {
  if (icon !== 'geladeira') return <Text style={{ fontSize: size * 0.85 }}>{icon}</Text>;
  return (
    <Svg width={size} height={size} viewBox="0 0 40 40">
      <Rect x={9} y={3} width={22} height={34} rx={4} fill="#E5E7EB" stroke={cor} strokeWidth={2} />
      <Line x1={9} y1={15} x2={31} y2={15} stroke={cor} strokeWidth={2} />
      <Rect x={12} y={7} width={2.5} height={5} rx={1} fill={cor} />
      <Rect x={12} y={18} width={2.5} height={8} rx={1} fill={cor} />
      <Circle cx={26} cy={33} r={1} fill="#9CA3AF" />
    </Svg>
  );
}

export default function Casa({ item, onBack }: { item?: string; onBack: () => void }) {
  const casa = useDoc('casa');
  const rooms = casa.rooms;
  const tarefas = tarefasCasa(casa);
  const { boxes } = useDoc('anotacoes');
  const { corDe } = useCores();
  const cor = corDe('casa');
  const cx = useCaixas();   // "Tamanho das caixas" (Grande = como antes)
  const [form, setForm] = useState<{ id: string | null; name: string; icon: string; manual: boolean } | null>(null);
  const [icones, setIcones] = useState(false);
  const [menu, setMenu] = useState<{ t: string; a: Action[] } | null>(null);
  const [aberto, setAberto] = useState<string | null>(item ?? null);
  const [ficha, setFicha] = useState<TarefaCasa | null>(null);
  const [verFeitas, setVerFeitas] = useState(false);
  const org = useOrganizar('casa');
  const orgC = useOrganizar('casa-comodo');
  const { enviar } = useAcoes();
  useMeAvise(null);   // redesenha quando um ⏰ dos cômodos é marcado ou cancelado
  // o + flutuante: nova tarefa da casa ou novo cômodo (0.65)
  // texto 29: o + oferece tarefa, cômodo e Nota rápida
  const novoTela = useNovoComNota('casa', null, [
    { label: '🧹 ' + t('Nova tarefa da casa'), onPress: () => setFicha(novaTarefaCasa()) },
    { label: '🏠 ' + t('Novo cômodo'), onPress: () => setForm({ id: null, name: '', icon: '🏠', manual: false }) }], t('Adicionar'));
  const excluirTarefa = (x: TarefaCasa) => { paraLixeira('casa', 'lista', x.nome, x, { doc: 'casa', campo: 'tarefas' }); excluirTarefaCasa(x.id); };

  // uma vez só: as tarefas da casa que estavam no My Planner passam a ficar só aqui
  useEffect(() => {
    const n = migrarTarefasDoPlanner();
    if (n) Alert.alert(t('Tarefas da casa'), t('{n} tarefa(s) da casa saíram do My Planner e agora ficam só em Minha Casa. Para levar uma delas ao Planner, abra a tarefa e toque em "Enviar para o My Planner".', { n }));
  }, []);
  useEffect(() => { if (item) { const x = tarefas.find(y => y.id === item); if (x) setFicha(x); } }, [item]); // eslint-disable-line react-hooks/exhaustive-deps

  const salvar = () => {
    if (!form?.name.trim()) return Alert.alert(t('Dê um nome ao cômodo.'));
    const r: Room = { id: form.id ?? uid(), name: form.name.trim(), icon: form.icon };
    setDoc('casa', d => ({ ...d, rooms: form.id ? d.rooms.map(x => (x.id === r.id ? r : x)) : [...d.rooms, r] }));
    setForm(null);
  };
  const excluirComodo = (r: Room) => { paraLixeira('casa', 'lista', r.name, r, { doc: 'casa', campo: 'rooms' }); excluirComodo0(r); };
  const excluirComodo0 = (r: Room) => setDoc('casa', d => ({ ...d, rooms: d.rooms.filter(x => x.id !== r.id),
    tarefas: (d.tarefas ?? []).map(x => (x.comodo === r.id ? { ...x, comodo: null } : x)) }));
  /** Cópia do cômodo com cópias das tarefas dele (0.52). */
  const duplicarComodo = (r: Room) => setDoc('casa', d => {
    const novo: Room = { ...r, id: uid(), name: t('{titulo} (cópia)', { titulo: r.name }) };
    const i = d.rooms.findIndex(x => x.id === r.id);
    const rooms = [...d.rooms]; rooms.splice(i + 1, 0, novo);
    const copias = (d.tarefas ?? []).filter(x => x.comodo === r.id).map(x => ({ ...x, id: uid(), comodo: novo.id, ultima: null, antes: null, planner: null,
      subs: x.subs.map(y => ({ ...y, id: uid(), done: false })) }));
    return { ...d, rooms, tarefas: [...(d.tarefas ?? []), ...copias] };
  });
  const duplicarTarefa = (x: TarefaCasa) => salvarTarefaCasa({ ...x, id: uid(), nome: t('{titulo} (cópia)', { titulo: x.nome }), ultima: null, antes: null, planner: null,
    criado: today(), subs: x.subs.map(y => ({ ...y, id: uid(), done: false })), obs: x.obs.map(b => ({ ...b, id: uid(), items: b.items.map(i => ({ ...i, id: uid() })) })),
    ordem: x.ordem != null ? x.ordem + 0.5 : undefined, ordemComodo: x.ordemComodo != null ? x.ordemComodo + 0.5 : undefined });
  const opcoes = (r: Room) => setMenu({ t: r.name, a: [
    ...acoesDoMenu(enviar, { pacote: () => ({ titulo: r.name, emoji: '🏠', origem: 'casa', itens: doComodo(tarefas, r.id).map(x => x.nome) }), fixar: { on: orgC.fixado(r.id), alternar: () => orgC.alternarFixo(r.id) }, lembrar: { aba: 'casa', id: r.id, titulo: r.name } }),
    { label: '📦 ' + t('Arquivar'), onPress: () => orgC.arquivar(r.id) },
    { label: '⧉ ' + t('Duplicar'), onPress: () => duplicarComodo(r) },
    { label: '➕ ' + t('Nova tarefa neste cômodo'), onPress: () => setFicha(novaTarefaCasa(r.id, '', r.icon === 'geladeira' ? '🍳' : r.icon)) },
    { label: t('Renomear ou trocar ícone'), onPress: () => setForm({ id: r.id, name: r.name, icon: r.icon, manual: true }) },
    { label: t('Excluir cômodo'), destructive: true, onPress: () => Alert.alert(t('Excluir {nome}?', { nome: r.name }), t('As tarefas do cômodo continuam em Minha Casa, sem cômodo.'), [
      { text: t('Cancelar'), style: 'cancel' }, { text: t('Excluir'), style: 'destructive', onPress: () => excluirComodo(r) }]) },
  ] });

  const tarefasAtivas = org.separar(tarefas, x => x.id);
  const grupos = gruposCasa(tarefasAtivas.ativos);
  const ordFix = (l: TarefaCasa[]) => [...l.filter(x => org.fixado(x.id)), ...l.filter(x => !org.fixado(x.id))];
  const selT = useSelecao({ itens: tarefasAtivas.ativos, chave: x => x.id, cor, onArquivar: ids => org.arquivar(ids),
    onExcluir: ids => ids.forEach(id => { const x = tarefas.find(y => y.id === id); if (x) excluirTarefa(x); }) });
  const comodos = orgC.separar(rooms, r => r.id);
  const nomeComodo = (id: string | null) => rooms.find(r => r.id === id)?.name ?? '';
  const NOMES: [Grupo, string, string][] = [['atrasadas', t('ATRASADAS'), C.danger], ['vencer', t('A VENCER (7 DIAS)'), C.warn], ['adiante', t('MAIS ADIANTE'), C.sub]];

  const linha = (x: TarefaCasa, compacta = false) => {
    const s = situacaoCasa(x);
    const feitaHoje = x.ultima === today();
    const txt = feitaHoje ? t('Feita hoje') : s.grupo === 'feitas' ? t('Feita em {data}', { data: brYmd(x.ultima!) })
      : s.dias < 0 ? t('Atrasada há {n} dia(s)', { n: -s.dias }) : s.dias === 0 ? t('Vence hoje') : t('Em {n} dia(s)', { n: s.dias });
    const corTxt = feitaHoje ? C.ok : s.dias < 0 ? C.danger : s.dias === 0 ? C.warn : C.sub;
    return (
      <AcoesItem lembrar={{ aba: 'casa', id: x.id, titulo: x.nome }} nome={x.nome} onExcluir={() => excluirTarefa(x)} onDuplicar={() => duplicarTarefa(x)} onArquivar={() => org.arquivar(x.id)}
        fixar={{ on: org.fixado(x.id), alternar: () => org.alternarFixo(x.id) }}
        pacote={() => ({ titulo: x.nome, emoji: x.emoji, itens: x.subs.map(y => y.text), texto: x.obs.filter(b => b.type === 'text').map(b => b.text).join('\n').trim()
          || x.obs.flatMap(b => b.items.map(i => i.text)).filter(Boolean).join('\n'), origem: 'casa', remover: () => excluirTarefaCasa(x.id) })}>
      <Pressable onPress={() => setFicha(x)} style={{ flexDirection: 'row', alignItems: 'center', gap: 10, backgroundColor: C.card, borderRadius: cx.raio(14), ...cx.pads(compacta ? 10 : 12) }}>
        <Pressable hitSlop={8} onPress={() => marcarFeita(x.id)} style={{ width: cx.v(28, 26, 22), height: cx.v(28, 26, 22), borderRadius: cx.v(14, 13, 11), borderWidth: 2, borderColor: cor,
          backgroundColor: feitaHoje ? cor : 'transparent', alignItems: 'center', justifyContent: 'center' }}>
          {feitaHoje && <Text style={{ color: '#fff', fontWeight: '900' }}>✓</Text>}
        </Pressable>
        <Text style={{ fontSize: cx.icone(20) }}>{x.emoji}</Text>
        <View style={{ flex: 1 }}>
          <Text style={{ color: C.text, fontWeight: '600' }} numberOfLines={2}>{x.nome || t('Sem nome')}</Text>
          <Text style={{ color: C.sub, fontSize: 11 }} numberOfLines={1}>
            <Text style={{ color: corTxt, fontWeight: '700' }}>{txt}</Text>{' · '}{recurLabel(x.recur, x.criado)}{!compacta && x.comodo ? ' · ' + nomeComodo(x.comodo) : ''}{x.planner ? ' · 📅' : ''}
          </Text>
        </View>
      </Pressable>
      </AcoesItem>
    );
  };

  return (
    <Screen title={t('Minha Casa')} onBack={onBack}>
      {/* Tarefas da casa, por atraso */}
      {/* texto 26 (C1): seções recolhíveis pelo título (as principais começam abertas); o app lembra */}
      <Recolhivel titulo={t('TAREFAS DA CASA')} visual="titulo" chave="casa:tarefas" inicial gap={cx.gap(8)} estiloBarra={{ marginTop: 4 }}
        resumo={String(tarefasAtivas.ativos.length)} direita={<Pressable onPress={() => setFicha(novaTarefaCasa())} hitSlop={8}><Text style={{ color: cor, fontWeight: '700' }}>+ {t('Tarefa')}</Text></Pressable>}>
      {!tarefas.length && <Text style={{ color: C.sub, fontSize: 13 }}>{t('Crie as tarefas da casa (limpar banheiro, trocar roupa de cama...) com a frequência. O app avisa quando estão atrasadas, sem encher o My Planner.')}</Text>}
      {selT.barra}
      {NOMES.map(([g, nome, c]) => grupos[g].length > 0 && (
        <View key={g} style={{ gap: cx.gap(6) }}>
          <Text style={{ color: c, fontSize: 11, fontWeight: '800', letterSpacing: 0.6 }}>{nome} · {grupos[g].length}</Text>
          <Ordenavel itens={ordFix(grupos[g].map(z => z.x))} chave={x => x.id} gap={cx.gap(6)} onOrdem={l => ordenarGrupo(l.map(x => x.id))} selecao={selT.prop} render={x => linha(x)} />
        </View>
      ))}
      <Arquivados itens={tarefasAtivas.arquivados} chave={x => x.id} cor={cor} qualificador={t('Tarefas')} render={x => <Text style={{ color: C.text }}>{x.emoji} {x.nome}</Text>}
        onRestaurar={x => org.restaurar(x.id)} onExcluir={x => Alert.alert(t('Excluir {nome}?', { nome: x.nome }), t('Fica 30 dias na lixeira.'), [{ text: t('Cancelar'), style: 'cancel' }, { text: t('Excluir'), style: 'destructive', onPress: () => excluirTarefa(x) }])} />
      {grupos.feitas.length > 0 && (
        <Pressable onPress={() => setVerFeitas(v => !v)}><Text style={{ color: C.sub, fontSize: 12 }}>{verFeitas ? '▾' : '▸'} {t('Feitas (não se repetem) · {n}', { n: grupos.feitas.length })}</Text></Pressable>
      )}
      {verFeitas && grupos.feitas.map(z => <View key={z.x.id}>{linha(z.x)}</View>)}
      </Recolhivel>

      {/* Cômodos: segurar e arrastar para qualquer direção, como os ícones da tela do celular */}
      <Recolhivel titulo={t('CÔMODOS')} visual="titulo" chave="casa:comodos" inicial gap={cx.gap(8)} resumo={String(comodos.ativos.length)}>
      {!rooms.length && (
        <View style={{ alignItems: 'center', gap: 8, padding: 30, backgroundColor: C.card, borderRadius: 18 }}>
          <Text style={{ fontSize: 40 }}>🏡</Text>
          <Text style={{ color: C.text, fontWeight: '700' }}>{t('Nenhum cômodo ainda')}</Text>
          <Btn cor={cor} label={'+ ' + t('Adicionar cômodo')} onPress={() => setForm({ id: null, name: '', icon: '🏠', manual: false })} />
        </View>
      )}
      <Ordenavel itens={comodos.ativos} chave={r => r.id} grade gap={cx.gap(12)} onOrdem={ordenarComodos}
        estiloItem={r => ({ width: aberto === r.id ? '100%' : '47.5%' })}
        render={r => {
          const ts = doComodo(tarefas, r.id);
          const sel = aberto === r.id;
          const atras = ts.filter(x => situacaoCasa(x).grupo === 'atrasadas').length;
          return (
            <ZonaSoltar grupo="casa-tar" id={r.id} corDestaque={cor} style={{ borderRadius: cx.raio(20) }}>
              <ComodoSwipe ativo={!sel} nome={r.name} onExcluir={() => excluirComodo(r)} onDuplicar={() => duplicarComodo(r)} onArquivar={() => orgC.arquivar(r.id)}>
              <Pressable onPress={() => setAberto(sel ? null : r.id)}
                style={{ backgroundColor: alpha(cor, 0.12), borderRadius: cx.raio(20), ...cx.pads(14), gap: cx.gap(8), borderWidth: 1, borderColor: sel ? cor : alpha(cor, 0.3) }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                  <RoomIcon icon={r.icon} cor={cor} size={cx.alt(36)} />
                  <Text style={{ color: C.text, fontWeight: '700', flex: 1 }} numberOfLines={2}>{r.name}</Text>
                  {orgC.fixado(r.id) && <Text style={{ fontSize: 11 }}>⭐</Text>}
                  {temAviso({ aba: 'casa', id: r.id }) && <Text style={{ fontSize: 11 }}>⏰</Text>}
                  <Pressable onPress={() => opcoes(r)} hitSlop={10} accessibilityLabel={t('Mais opções')}><Text style={{ color: C.sub, fontSize: 18, fontWeight: '800' }}>⋮</Text></Pressable>
                </View>
                <Text style={{ color: atras ? C.danger : C.sub, fontSize: 12 }}>{ts.length ? t('{n} tarefa(s)', { n: ts.length }) + (atras ? ' · ' + t('{n} atrasada(s)', { n: atras }) : '') : t('Sem tarefas')}</Text>
                {sel && (
                  <View style={{ gap: cx.gap(6) }}>
                    <Ordenavel itens={ts} chave={x => x.id} gap={cx.gap(6)} grupo="casa-tar" listaId={r.id}
                      onOrdem={l => ordenarNoComodo(l.map(x => x.id))} onMover={(x, destino) => moverParaComodo(x.id, destino)}
                      render={x => linha(x, true)} />
                    <Pressable onPress={() => setFicha(novaTarefaCasa(r.id, '', r.icon === 'geladeira' ? '🍳' : r.icon))}>
                      <Text style={{ color: cor, fontWeight: '600', padding: 6 }}>+ {t('Tarefa para {nome}', { nome: r.name })}</Text>
                    </Pressable>
                  </View>
                )}
              </Pressable>
              </ComodoSwipe>
            </ZonaSoltar>
          );
        }} />
      </Recolhivel>
      <Arquivados itens={comodos.arquivados} chave={r => r.id} cor={cor} qualificador={t('Cômodos')} render={r => <Text style={{ color: C.text }}>{r.name}</Text>}
        onRestaurar={r => orgC.restaurar(r.id)} onExcluir={r => Alert.alert(t('Excluir {nome}?', { nome: r.name }), t('As tarefas do cômodo continuam em Minha Casa, sem cômodo.') + '\n\n' + t('Fica 30 dias na lixeira.'), [{ text: t('Cancelar'), style: 'cancel' }, { text: t('Excluir'), style: 'destructive', onPress: () => excluirComodo(r) }])} />
      {rooms.length > 1 && <Text style={{ color: C.mute, fontSize: 11, textAlign: 'center' }}>{t('Segure um cômodo para mudar de lugar. Com o cômodo aberto, segure uma tarefa e solte em outro cômodo para levá-la.')}</Text>}
      <ReceivedBoxes boxes={boxes} abaId="casa" />
      {novoTela}

      {ficha && <TarefaCasaSheet tarefa={ficha} rooms={rooms} cor={cor} onClose={() => setFicha(null)} />}

      <Sheet visible={!!form} onClose={() => setForm(null)} title={form?.id ? t('Editar cômodo') : t('Adicionar cômodo')}
        right={<Pressable onPress={salvar}><Text style={{ color: cor, fontWeight: '800' }}>{t('Salvar')}</Text></Pressable>}>
        {form && (
          <ScrollView {...ROLAGEM_TECLADO} keyboardShouldPersistTaps="handled" contentContainerStyle={{ padding: 16, gap: 12, paddingBottom: 60 }}>
            <Text style={{ color: C.sub, fontSize: 12, fontWeight: '700', letterSpacing: 0.8 }}>{t('CRIAR CÔMODO')}</Text>
            <View style={{ flexDirection: 'row', gap: 10, alignItems: 'center' }}>
              <Pressable onPress={() => setIcones(v => !v)} style={{ width: 56, height: 56, borderRadius: 16, backgroundColor: alpha(cor, 0.18), alignItems: 'center', justifyContent: 'center', borderWidth: 2, borderColor: cor }}>
                <RoomIcon icon={form.icon} cor={cor} />
              </Pressable>
              <Field value={form.name} placeholder={t('Nome do cômodo')} style={{ flex: 1 }}
                onChangeText={v => setForm({ ...form, name: v, icon: form.manual ? form.icon : iconeDoNome(v) })} />
            </View>
            {icones && (
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6, backgroundColor: C.card, padding: 10, borderRadius: 14 }}>
                {ICONES.map(ic => (
                  <Pressable key={ic} onPress={() => { setForm({ ...form, icon: ic, manual: true }); setIcones(false); }}
                    style={{ width: 46, height: 46, alignItems: 'center', justifyContent: 'center', borderRadius: 12, backgroundColor: form.icon === ic ? alpha(cor, 0.3) : 'transparent' }}>
                    <RoomIcon icon={ic} size={32} cor={cor} />
                  </Pressable>
                ))}
              </View>
            )}
            {!form.id && (<>
              <Text style={{ color: C.sub, fontSize: 12, fontWeight: '700', letterSpacing: 0.8, marginTop: 8 }}>{t('OU ESCOLHER CÔMODO…')}</Text>
              <View style={{ backgroundColor: C.card, borderRadius: 16, maxHeight: 360, overflow: 'hidden' }}>
                <ScrollView nestedScrollEnabled>
                  {PRESETS.map(([chave, ic]) => {
                    const nome = t(chave);
                    return (
                    <Pressable key={chave} onPress={() => setForm({ ...form, name: nome, icon: ic, manual: false })}
                      style={{ flexDirection: 'row', alignItems: 'center', gap: 12, padding: 12, backgroundColor: form.name === nome ? alpha(cor, 0.2) : 'transparent' }}>
                      <RoomIcon icon={ic} size={30} cor={cor} />
                      <Text style={{ color: C.text, flex: 1 }}>{nome}</Text>
                      {form.name === nome && <Text style={{ color: cor }}>✓</Text>}
                    </Pressable>
                    );
                  })}
                </ScrollView>
              </View>
            </>)}
            <Btn cor={cor} label={t('Salvar cômodo')} onPress={salvar} />
          </ScrollView>
        )}
      </Sheet>
      <ActionSheet title={menu?.t ?? ''} actions={menu?.a ?? null} onClose={() => setMenu(null)} />
    </Screen>
  );
}

/** Caixa de observações com checklist, lista e texto livre (como as caixas de Anotações). */
function Blocos({ blocos, cor, onChange }: { blocos: Block[]; cor: string; onChange: (b: Block[]) => void }) {
  const setItems = (bid: string, fn: (it: Item[]) => Item[]) => onChange(blocos.map(b => (b.id === bid ? { ...b, items: fn(b.items) } : b)));
  const atual = useRef(blocos); atual.current = blocos;
  const [menuB, setMenuB] = useState<{ t: string; a: Action[] } | null>(null);
  const mexer = (id: string, d: number) => { const i = blocos.findIndex(b => b.id === id), j = i + d; if (i < 0 || j < 0 || j >= blocos.length) return; const a = [...blocos]; [a[i], a[j]] = [a[j], a[i]]; onChange(a); };
  // REGRAS 4.1: vazio sai direto, com texto pergunta; sempre com Desfazer (o último item leva o bloco junto)
  const tirarItem = (bl: Block, it: Item, n: number) => excluirPequeno(!!it.text.trim(), () => {
    if (bl.items.length > 1) { setItems(bl.id, l => l.filter(x => x.id !== it.id)); return () => onChange(atual.current.map(b => (b.id === bl.id && !b.items.some(x => x.id === it.id) ? { ...b, items: inserirEm(b.items, n, it) } : b))); }
    return tirarBloco(bl);
  });
  const tirarBloco = (bl: Block) => {
    const pos = blocos.findIndex(b => b.id === bl.id);
    onChange(blocos.filter(b => b.id !== bl.id));
    return () => { if (!atual.current.some(b => b.id === bl.id)) onChange(inserirEm(atual.current, pos, bl)); };
  };
  return (
    <View style={{ gap: 6 }}>
      {blocos.map(bl => bl.type === 'text' ? (
        <View key={bl.id} style={{ flexDirection: 'row', gap: 6 }}>
          <TextInput multiline value={bl.text} placeholder={t('Texto livre')} placeholderTextColor={C.mute}
            onChangeText={v => onChange(blocos.map(b => (b.id === bl.id ? { ...b, text: v } : b)))}
            style={{ flex: 1, color: C.text, backgroundColor: C.card2, borderRadius: 10, padding: 10, minHeight: 60, textAlignVertical: 'top' }} />
          <Ditado pequeno cor={C.sub} onTexto={tx => onChange(blocos.map(b => (b.id === bl.id && b.type === 'text' ? { ...b, text: b.text ? b.text + ' ' + tx : tx } : b)))} />
          {/* texto 26 (REGRAS 3.1/3.4): sem ✕ solto; o ⋮ do bloco tem Duplicar, Subir, Descer e Excluir */}
          <Pressable hitSlop={8} onPress={() => setMenuB({ t: t('Texto livre'), a: [
            { label: '⧉ ' + t('Duplicar'), onPress: () => { const i = blocos.findIndex(b => b.id === bl.id); onChange(inserirEm(blocos, i + 1, { ...bl, id: uid() })); } },
            { label: '↑ ' + t('Subir'), onPress: () => mexer(bl.id, -1) },
            { label: '↓ ' + t('Descer'), onPress: () => mexer(bl.id, 1) },
            { label: t('Excluir'), destructive: true, onPress: () => excluirPequeno(!!bl.text.trim(), () => tirarBloco(bl), t('Excluir este bloco?')) },
          ] })} accessibilityLabel={t('Opções do bloco')}><Text style={{ color: C.sub, fontSize: 18, fontWeight: '800', paddingHorizontal: 4 }}>⋮</Text></Pressable>
        </View>
      ) : bl.type === 'desenho' ? null : (
        // texto 26 (REGRAS 2): sem ⠿; segurar na parte livre e arrastar move; deslizar → exclui, ← duplica
        <Ordenavel key={bl.id} itens={bl.items} chave={i => i.id} alca gap={0} onOrdem={l => setItems(bl.id, () => l)} render={(it, n, a) => (
          <ItemDeslizar onExcluir={() => tirarItem(bl, it, n)} onDuplicar={() => setItems(bl.id, l => inserirEm(l, n + 1, { ...it, id: uid() }))}>
          <View {...a.alca} style={{ flexDirection: 'row', alignItems: 'center', gap: 8, backgroundColor: C.card, paddingLeft: 4 }}>
            {bl.type === 'check' ? (
              <Pressable hitSlop={8} onPress={() => setItems(bl.id, l => l.map(x => (x.id === it.id ? { ...x, done: !x.done } : x)))}
                style={{ width: 20, height: 20, borderRadius: 6, borderWidth: 2, borderColor: it.done ? cor : C.mute, backgroundColor: it.done ? cor : 'transparent', alignItems: 'center', justifyContent: 'center' }}>
                {it.done && <Text style={{ color: '#fff', fontSize: 11, fontWeight: '900' }}>✓</Text>}</Pressable>
            ) : <Text style={{ color: cor, fontSize: 18, width: 20, textAlign: 'center' }}>•</Text>}
            <TextInput {...semArrastar} value={it.text} placeholder={bl.type === 'check' ? t('Item') : t('Lista')} placeholderTextColor={C.mute} blurOnSubmit={false}
              style={{ flex: 1, color: C.text, paddingVertical: 7 }}
              onChangeText={v => setItems(bl.id, l => l.map(x => (x.id === it.id ? { ...x, text: v } : x)))}
              onSubmitEditing={() => setItems(bl.id, l => [...l, { id: uid(), text: '', done: false }])} />
          </View>
          </ItemDeslizar>
        )} />
      ))}
      <View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap' }}>
        {([['check', '☑ ' + t('Checklist')], ['list', '• ' + t('Lista')], ['text', t('Texto livre')]] as const).map(([k, l]) => (
          <Pressable key={k} onPress={() => onChange([...blocos, novoBloco(k)])} style={{ paddingHorizontal: 10, paddingVertical: 6, borderRadius: 12, backgroundColor: C.card2 }}>
            <Text style={{ color: C.sub, fontSize: 12 }}>{l}</Text>
          </Pressable>
        ))}
      </View>
      <ActionSheet title={menuB?.t ?? ''} actions={menuB?.a ?? null} onClose={() => setMenuB(null)} />
    </View>
  );
}

/** Ficha simples da tarefa da casa: nome, frequência, subtarefas e observações. */
function TarefaCasaSheet({ tarefa, rooms, cor, onClose }: { tarefa: TarefaCasa; rooms: Room[]; cor: string; onClose: () => void }) {
  const sheet = useTaskSheet();
  const existe = tarefasCasa().some(x => x.id === tarefa.id);
  const [x, setX] = useState(tarefa);
  const [freq, setFreq] = useState(false);
  const [emoji, setEmoji] = useState(false);
  const [sub, setSub] = useState('');
  const final = () => ({ ...x, nome: x.nome.trim(), subs: [...x.subs.filter(s => s.text.trim()), ...(sub.trim() ? [{ id: uid(), text: sub.trim(), done: false }] : [])] });
  const salvar = () => {
    if (!x.nome.trim()) return Alert.alert(t('Dê um nome à tarefa.'));
    salvarTarefaCasa(final()); onClose();
  };
  const enviarPlanner = () => {
    if (!x.nome.trim()) return Alert.alert(t('Dê um nome à tarefa.'));
    const f = final(); salvarTarefaCasa(f);
    const room = rooms.find(r => r.id === f.comodo);
    const notas = f.obs.flatMap(b => (b.type === 'text' ? (b.text.trim() ? [b.text.trim()] : []) : b.items.filter(i => i.text.trim()).map(i => '• ' + i.text.trim()))).join('\n');
    onClose();
    setTimeout(() => sheet.open({ prefill: { title: f.nome, emoji: f.emoji, emojiManual: true, area: 'casa', recur: f.recur, notes: notas,
      subtasks: f.subs.map(s => ({ id: uid(), text: s.text })), ...(room ? { link: { aba: 'casa', id: room.id, label: room.name } } : {}) },
      onSaved: tk => ligarPlanner(f.id, tk.id) }), 350);
  };
  const s = situacaoCasa(x);
  return (
    <Sheet visible onClose={onClose} title={existe ? t('Tarefa da casa') : t('Nova tarefa da casa')} right={<Pressable onPress={salvar}><Text style={{ color: cor, fontWeight: '800' }}>{t('Salvar')}</Text></Pressable>}>
      <ScrollArrastavel keyboardShouldPersistTaps="handled" contentContainerStyle={{ padding: 16, gap: 12, paddingBottom: 60 }}>
        <View style={{ flexDirection: 'row', gap: 10, alignItems: 'center' }}>
          <Pressable onPress={() => setEmoji(true)} style={{ width: 52, height: 52, borderRadius: 14, backgroundColor: alpha(cor, 0.2), alignItems: 'center', justifyContent: 'center' }}>
            <Text style={{ fontSize: 28 }}>{x.emoji}</Text></Pressable>
          <Field value={x.nome} placeholder={t('Nome da tarefa (ex.: Limpar o banheiro)')} style={{ flex: 1 }} autoFocus={!existe} onChangeText={nome => setX({ ...x, nome })} />
        </View>
        {rooms.length > 0 && (
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
            <Chip small cor={cor} on={!x.comodo} label={t('Sem cômodo')} onPress={() => setX({ ...x, comodo: null })} />
            {rooms.map(r => <Chip key={r.id} small cor={cor} on={x.comodo === r.id} label={r.name} onPress={() => setX({ ...x, comodo: r.id })} />)}
          </View>
        )}
        <Row icon="🔄" label={t('Frequência')} value={recurLabel(x.recur, x.criado)} open={freq} onPress={() => setFreq(v => !v)}>
          <FrequenciaEditor recur={x.recur} start={x.criado} cor={cor} onChange={recur => setX({ ...x, recur })} />
          <Btn cor={cor} label={t('Pronto')} onPress={() => setFreq(false)} />
        </Row>
        <Text style={{ color: C.sub, fontSize: 12 }}>
          {t('Criada em {data}', { data: brYmd(x.criado) })}{x.ultima ? ' · ' + t('última vez em {data}', { data: brYmd(x.ultima) }) : ''}
          {s.prox ? ' · ' + (s.dias < 0 ? t('atrasada há {n} dia(s)', { n: -s.dias }) : t('próxima em {data}', { data: brYmd(s.prox) })) : ''}
        </Text>

        <Text style={{ color: C.sub, fontSize: 12, fontWeight: '700', letterSpacing: 0.8, marginTop: 6 }}>{t('SUBTAREFAS')}</Text>
        <Ordenavel itens={x.subs} chave={i => i.id} alca gap={0} onOrdem={subs => setX({ ...x, subs })} render={(it, n, a) => {
          const tirar = () => excluirPequeno(!!it.text.trim(), () => { setX(v => ({ ...v, subs: v.subs.filter(s2 => s2.id !== it.id) })); return () => setX(v => (v.subs.some(s2 => s2.id === it.id) ? v : { ...v, subs: inserirEm(v.subs, n, it) })); });
          return (
          <ItemDeslizar onExcluir={tirar} onDuplicar={() => setX(v => ({ ...v, subs: inserirEm(v.subs, n + 1, { ...it, id: uid() }) }))}>
          <View {...a.alca} style={{ flexDirection: 'row', alignItems: 'center', gap: 8, backgroundColor: C.card, borderRadius: 10, paddingHorizontal: 6 }}>
            <Pressable hitSlop={8} onPress={() => { setX({ ...x, subs: x.subs.map(s2 => (s2.id === it.id ? { ...s2, done: !s2.done } : s2)) }); if (existe) marcarSub(x.id, it.id); }}
              style={{ width: 22, height: 22, borderRadius: 6, borderWidth: 2, borderColor: it.done ? cor : C.mute, backgroundColor: it.done ? cor : 'transparent', alignItems: 'center', justifyContent: 'center' }}>
              {it.done && <Text style={{ color: '#fff', fontSize: 12, fontWeight: '900' }}>✓</Text>}</Pressable>
            <TextInput {...semArrastar} value={it.text} placeholderTextColor={C.mute} style={{ flex: 1, color: C.text, paddingVertical: 10 }}
              onChangeText={v => setX({ ...x, subs: x.subs.map(s2 => (s2.id === it.id ? { ...s2, text: v } : s2)) })} />
          </View>
          </ItemDeslizar>
        ); }} />
        <Field value={sub} onChangeText={setSub} placeholder={t('+ Adicionar subtarefa')} blurOnSubmit={false}
          onSubmitEditing={() => { if (sub.trim()) { setX({ ...x, subs: [...x.subs, { id: uid(), text: sub.trim(), done: false }] }); setSub(''); } }} />

        <Text style={{ color: C.sub, fontSize: 12, fontWeight: '700', letterSpacing: 0.8, marginTop: 6 }}>{t('OBS')}</Text>
        <View style={{ backgroundColor: C.card, borderRadius: 14, padding: 10 }}>
          <Blocos blocos={x.obs} cor={cor} onChange={obs => setX({ ...x, obs })} />
        </View>

        <Btn cor={cor} label={t('Salvar tarefa')} onPress={salvar} />
        {existe && <Btn cor={cor} outline label={(x.ultima === today() ? '↺ ' + t('Desmarcar feita hoje') : '✓ ' + t('Marcar como feita hoje'))} onPress={() => { marcarFeita(x.id); onClose(); }} />}
        <Btn cor={cor} outline label={'📅 ' + t('Enviar para o My Planner')} onPress={enviarPlanner} />
        <Text style={{ color: C.mute, fontSize: 11 }}>{t('A tarefa da casa não entra no My Planner sozinha. "Enviar para o My Planner" abre a ficha completa para escolher dia, horário, frequência, pausas e alertas.')}</Text>
        {existe && (
          <Pressable style={{ padding: 10 }} onPress={() => Alert.alert(t('Excluir {nome}?', { nome: x.nome }), '', [{ text: t('Cancelar'), style: 'cancel' },
            { text: t('Excluir'), style: 'destructive', onPress: () => { paraLixeira('casa', 'lista', x.nome, x, { doc: 'casa', campo: 'tarefas' }); excluirTarefaCasa(x.id); onClose(); } }])}>
            <Text style={{ color: C.danger, textAlign: 'center' }}>{t('Excluir tarefa')}</Text></Pressable>
        )}
      </ScrollArrastavel>
      <EmojiPicker visible={emoji} cor={cor} onClose={() => setEmoji(false)} onPick={e => setX(y => ({ ...y, emoji: e }))} />
    </Sheet>
  );
}

/** Cômodo fechado: arrastar para a direita exclui (as tarefas continuam, sem cômodo). Aberto, só as tarefas de dentro deslizam. */
function ComodoSwipe({ ativo, nome, onExcluir, onDuplicar, onArquivar, children }: { ativo: boolean; nome: string; onExcluir: () => void; onDuplicar: () => void; onArquivar: () => void; children: React.ReactNode }) {
  const cx = useCaixas();
  if (!ativo) return <>{children}</>;
  return <AcoesItem raio={cx.raio(20)} nome={nome} aviso={t('As tarefas do cômodo continuam em Minha Casa, sem cômodo.')} onExcluir={onExcluir} onDuplicar={onDuplicar} onArquivar={onArquivar} menuProprio>{children}</AcoesItem>;
}
